import type React from "react";

import { $enum } from "ts-enum-util";

import {
  CovSensorParamPeriodDtoType,
  CovSensorStatus,
  SoundSensorStatus,
  type PatchBuildingRoomsRequestBody,
} from "@eisox/api/core-api";
import { Alert, Button, cn, Select, Switch } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { HeatIcon, LeafIcon, PlusIcon } from "@eisox/icons";
import { colors } from "@eisox/tokens";
import { getDirtyValues } from "@eisox/tools";
import { useSelector } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { roomApi } from "~/api";
import { PlanningsGenerationDialog } from "~/components/plannings-generation-dialog";
import { UnusualAbsenceDialog } from "~/components/unusual-absence-dialog";
import { VersionGate } from "~/components/version-gate";
import { organizePlanningsByWeekDays, UpdatePlanningDialog, Week } from "~/features/planning";
import {
  COV_SENSOR_UPDATE_PLANNING_TYPES,
  covFormPeriodsToPlanning,
  functionsSettingsSchema,
  getCovSensorStatusLabel,
} from "~/features/room/helpers";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";
import { useRoomContext } from "~/providers/room.provider";

const NAME = "FunctionsSettings";

export const FunctionsSettings: React.FC = () => {
  const queryClient = useQueryClient();

  const { building, permissions } = useBuildingContext(NAME);
  const { room, valves } = useRoomContext(NAME);

  const { mutateAsync: updateRoom } = useMutation({
    mutationFn: (data: Omit<PatchBuildingRoomsRequestBody[number], "ids">) =>
      roomApi.update(building.id, [{ ids: [room.id], ...data }]),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: roomApi.queries.building(building.id).queryKey });
    },
  });

  const form = useAppForm({
    defaultValues: {
      isSwitchEnabled: room.isSwitchEnabled ?? false,
      isAutoPrecomf: room.isAutoPrecomf ?? false,
      isPreComfRoom: room.isPreComfRoom ?? false,
      covSensorParam: {
        status: room.covSensorParam.status,
        periods: room.covSensorParam.periods,
      },
      soundSensorParam: {
        status: room.soundSensorParam.status,
      },
    },
    validators: {
      onSubmit: functionsSettingsSchema,
    },
    onSubmit: async ({ value, formApi }) => {
      const dirty = getDirtyValues(formApi.options.defaultValues, value);
      if (!dirty) return;
      const { covSensorParam, soundSensorParam, ...rest } = dirty;
      await updateRoom({
        ...rest,
        ...(covSensorParam !== undefined && { covSensorParam: value.covSensorParam }),
        ...(soundSensorParam !== undefined && { soundSensorParam: value.soundSensorParam }),
      });
      formApi.reset();
    },
  });

  const covSensorParamStatus = useSelector(form.store, state => state.values.covSensorParam.status);

  const valveVersions = valves.map(valve => valve.softwareVersion);
  const gatewayVersions = valves.map(valve => valve.gatewaySoftwareVersion);

  return (
    <form
      data-testid={NAME}
      className="flex h-full flex-col justify-between"
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        <div className="flex h-full flex-col gap-[10px] overflow-y-auto">
          <div className="flex flex-col gap-[10px]">
            <p className="font-semibold flex items-center gap-[10px] text-base text-primary-blue">
              {m.button_settings()}
            </p>
            <form.AppField name="isSwitchEnabled">
              {field => <field.Switch disabled={!permissions.room?.isSwitchEnabled?.update} />}
            </form.AppField>
          </div>
          <div className="flex flex-col gap-[10px]">
            <p className="font-semibold flex items-center gap-[10px] text-base text-primary-blue">
              {m.unusual_absence_detection()} <LeafIcon color={colors.primary.green} width={20} height={20} />
            </p>
            <Alert severity="help">
              <p className="text-base italic">
                {m.unusual_absence_detection_help()}{" "}
                <UnusualAbsenceDialog
                  absenceTemperature={building.absenceTemperature}
                  preComfortTemperature={building.preComfortTemperature}
                  comfortTemperature={room.comfortTemperature}
                >
                  <Button type="button">{m.learn_more()}</Button>
                </UnusualAbsenceDialog>
              </p>
            </Alert>

            <form.AppField name="isAutoPrecomf">
              {field => <field.Switch disabled={!permissions.room?.isAutoPrecomf?.update} />}
            </form.AppField>
          </div>
          <div className="flex flex-col gap-[10px]">
            <p className="font-semibold flex items-center gap-[10px] text-base text-primary-blue">
              {m.pre_comfort_timeslot_generation()} <HeatIcon color={colors.primary.orange} width={20} height={20} />
            </p>
            <Alert severity="help">
              <p className="text-base italic">
                {m.precomfort_timeslot_generation_help()}{" "}
                <PlanningsGenerationDialog
                  absenceTemperature={building.absenceTemperature}
                  preComfortTemperature={building.preComfortTemperature}
                  comfortTemperature={room.comfortTemperature}
                >
                  <Button type="button">{m.learn_more()}</Button>
                </PlanningsGenerationDialog>
              </p>
            </Alert>
            <form.AppField name="isPreComfRoom">
              {field => <field.Switch disabled={!permissions.room?.isPreComfRoom?.update} />}
            </form.AppField>
          </div>

          <div className="flex flex-col gap-[10px]">
            <p className="font-semibold flex items-center gap-[10px] text-base text-primary-blue">{m.sound_sensor()}</p>
            <Alert severity="help">
              <p className="text-base italic">{m.sound_sensor_param_help()}</p>
            </Alert>
            <form.AppField name="soundSensorParam.status">
              {field => (
                <VersionGate
                  devices={[
                    {
                      type: "valve",
                      current: valveVersions,
                      feature: "SOUND_SENSOR_PARAM",
                    },
                    {
                      type: "gateway",
                      current: gatewayVersions,
                      feature: "SOUND_SENSOR_PARAM",
                    },
                  ]}
                >
                  {({ disabled }) => (
                    <label className="flex flex-row items-center justify-between gap-[10px]">
                      <span className="font-normal text-base italic">
                        {m.activation({
                          activation: field.state.value === SoundSensorStatus.ENABLED ? "true" : "false",
                        })}
                      </span>
                      <Switch
                        checked={field.state.value === SoundSensorStatus.ENABLED}
                        onCheckedChange={checked =>
                          field.handleChange(checked ? SoundSensorStatus.ENABLED : SoundSensorStatus.DISABLED)
                        }
                        disabled={disabled}
                      />
                    </label>
                  )}
                </VersionGate>
              )}
            </form.AppField>
          </div>

          <div className={cn("flex flex-col gap-[10px]", covSensorParamStatus === CovSensorStatus.PERIODS && "flex-1")}>
            <p className="font-semibold flex items-center gap-[10px] text-base text-primary-blue">{m.air_sensor()}</p>
            <form.AppField name="covSensorParam.status">
              {field => (
                <VersionGate
                  devices={[
                    {
                      type: "valve",
                      current: valveVersions,
                      feature: "COV_SENSOR_PARAM",
                    },
                    {
                      type: "gateway",
                      current: gatewayVersions,
                      feature: "COV_SENSOR_PARAM",
                    },
                  ]}
                >
                  {({ disabled }) => (
                    <Select.Root
                      multiple={false}
                      value={field.state.value}
                      onValueChange={value => {
                        if (
                          covSensorParamStatus === CovSensorStatus.PERIODS &&
                          room.covSensorParam.periods.length > 0
                        ) {
                          form.setFieldValue("covSensorParam.periods", room.covSensorParam.periods);
                        }
                        field.handleChange(value as CovSensorStatus);
                      }}
                    >
                      <Select.Trigger disabled={disabled}>
                        <Select.Value />
                      </Select.Trigger>
                      <Select.Content side="top">
                        <Select.Viewport>
                          {$enum(CovSensorStatus)
                            .getValues()
                            .map(status => (
                              <Select.Item key={status} value={status}>
                                {getCovSensorStatusLabel(status)}
                              </Select.Item>
                            ))}
                        </Select.Viewport>
                      </Select.Content>
                    </Select.Root>
                  )}
                </VersionGate>
              )}
            </form.AppField>

            {covSensorParamStatus === CovSensorStatus.PRESENCE && (
              <Alert severity="help">
                <p className="text-base italic">{m.cov_sensor_param_help_presence()}</p>
              </Alert>
            )}

            {covSensorParamStatus === CovSensorStatus.PERIODS && (
              <form.AppField name="covSensorParam.periods">
                {field => {
                  const periods = field.state.value;

                  const planningPeriods = covFormPeriodsToPlanning(periods);
                  const { reorderedDays, timeslotsByDay } = organizePlanningsByWeekDays(planningPeriods);

                  return (
                    <div className="flex flex-1 flex-col gap-[10px]">
                      <div className="flex items-center justify-between gap-[10px]">
                        <span className="font-medium text-base">{m.planning()}</span>
                        <UpdatePlanningDialog
                          userDefinedPlannings={planningPeriods}
                          types={[...COV_SENSOR_UPDATE_PLANNING_TYPES]}
                          onValueChange={v =>
                            Promise.resolve(
                              field.handleChange(v.map(p => ({ ...p, type: CovSensorParamPeriodDtoType.enabled }))),
                            )
                          }
                        >
                          <Button type="button" disabled={periods.length >= 14}>
                            <PlusIcon />
                          </Button>
                        </UpdatePlanningDialog>
                      </div>
                      <Week.Root>
                        {reorderedDays.map(day => (
                          <Week.Day key={day} day={day as 0 | 1 | 2 | 3 | 4 | 5 | 6}>
                            {(timeslotsByDay[day] ?? []).map((timeslot, index) => (
                              <UpdatePlanningDialog
                                key={index}
                                userDefinedPlannings={planningPeriods}
                                types={[...COV_SENSOR_UPDATE_PLANNING_TYPES]}
                                value={timeslot}
                                onValueChange={v =>
                                  Promise.resolve(
                                    field.handleChange(
                                      v.map(p => ({ ...p, type: CovSensorParamPeriodDtoType.enabled })),
                                    ),
                                  )
                                }
                              >
                                <Week.Timeslot
                                  start={timeslot.hourStart}
                                  end={timeslot.hourEnd}
                                  type={timeslot.type}
                                  userDefined
                                />
                              </UpdatePlanningDialog>
                            ))}
                          </Week.Day>
                        ))}
                      </Week.Root>
                    </div>
                  );
                }}
              </form.AppField>
            )}
          </div>
          <form.SubmitButton className="m-0 mx-auto" label={m.save()} />
        </div>
      </form.AppForm>
    </form>
  );
};

FunctionsSettings.displayName = NAME;
